import { useEffect, useState } from 'react';

/**
 * Tiny localStorage-backed `useState`. Reads the value once on mount and
 * writes back on every change. Tolerates a missing or unparseable entry
 * by falling back to `initial`. Same-tab consumers of the same key are
 * NOT auto-synced — each call has its own React state. For UI prefs
 * where one component owns the value (canvas width, rail collapsed)
 * that's fine.
 */
export function usePersistedState<T>(
  key: string,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw == null) return initial;
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // localStorage may throw on quota / disabled — degrade silently.
    }
  }, [key, value]);

  return [value, setValue];
}
